import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { FlyoutOrderControl } from '../../../../components/flyout-components/FlyoutOrderControl';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { FlyoutTitle } from '../../../../components/flyout-components/FlyoutTitle';
import * as Forms from '../../../../components/form-elements/_index';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { LocalTimeRead } from '../../../../models/utilities/local-time-read';
import { ServiceCenter } from '../../../../models/utilities/service-center';
import { serviceCenterSelectList } from '../../../../selectors/userdata-selectors';
import { DealerSvc } from '../../../../shared/services/dealer-service';
import { IApplicationState } from '../../../../store/IApplicationState';
import { StatusCode } from '../../../../utils/api';
import { GlobalConstants } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { Mapper } from '../../../../utils/mapper';
import { ModelValidator } from '../../../../utils/model-validator';
import { ShipmentMethod } from '../../../../transportation/models/enums/shipment-method';
import { PickupCreate } from '../../../../transportation/models/pickups/pickup-create';
import { PickupRead } from '../../../../transportation/models/pickups/pickup-read';
import { PickupUpdate } from '../../../../transportation/models/pickups/pickup-update';
import { OrderSummary } from '../../../../transportation/models/shared/order-summary';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps extends IFlyOutProps {
    visible: boolean;
    pickup: PickupRead;
}

export const PickupActionEditInfo = (props: IProps) => {

    const store                                 = useStore();
    const dispatch                              = useDispatch();
    const userServiceCenter                     = useServiceCenter();    
    const [service]                             = React.useState(new TransportationSvc());
    const [dealerSvc]                           = React.useState(new DealerSvc());
    const [isFetching, setIsFetching]           = React.useState(true);
    const [editPickup, setEditPickup]           = React.useState<PickupRead>(new PickupRead());
    const [errors, setErrors]                   = React.useState({} as any);
    const [saveAttempts, setSaveAttempts]       = React.useState(0);
    const [availableOrders, setAvailableOrders] = React.useState<OrderSummary[]>([]);
    const [selectedOrders, setSelectedOrders]   = React.useState<OrderSummary[]>([]);
    
    const [serviceCenters] = React.useState(() => {
        return serviceCenterSelectList(store.getState());
    });

    async function getDealerOrders(dealerId : string, serviceCenter : string) {
        if (dealerId.length > 0 && serviceCenter.length > 0) {
            const dealerOrders = await service.getPlannedDealerOrders(dealerId, serviceCenter, ShipmentMethod.PickUp);
            if (dealerOrders.status === StatusCode.Success) {
                setAvailableOrders(dealerOrders.data);
            }
            setIsFetching(false);
        }
        else {
            setIsFetching(false);
        }
    }
    
    React.useEffect(() => {
        if (props.visible) {

            const tmpPickup = {...props.pickup};

            if (props.pickup.dealerId.length > 0 && props.pickup.serviceCenterId.length === 0) {
                tmpPickup.serviceCenterId = userServiceCenter.name === "ALL" ? '' : userServiceCenter.name;
            }

            setEditPickup(tmpPickup);
            setIsFetching(true);
            getDealerOrders(tmpPickup.dealerId, tmpPickup.serviceCenterId);
            setSelectedOrders([...props.pickup.orders]); 
            return;
        }
    }, [props.visible]);

    /**
     * Get the right timezone
     */
    const getCorrectPickupTimeZone = () => {

        if (!AppUtils.IsEmpty(editPickup.pickupTime?.timeZone)) {
            return editPickup.pickupTime?.timeZone;
        } 
        else if (AppUtils.IsEmpty(editPickup.dealerId)) {
            return "";
        }
        else {

            const allSCs = (store.getState() as IApplicationState).utility.appServiceCenters;
            const currentSC = allSCs.find((itm : ServiceCenter) => {
                return itm.name === editPickup.serviceCenterId;
            });

            return currentSC ? currentSC.timezone : '';
        }               
    };
    
    /**
     * Service Center change handler
     */
    const onServiceCenterChange = async (e: any) => {
        e.preventDefault();

        const tmp = {...editPickup} as PickupRead;
        tmp.serviceCenterId = e.target.value;
        setEditPickup(tmp);

        await getDealerOrders(tmp.dealerId, tmp.serviceCenterId);
    
    };

    /**
     * Determine whether to disable the service center
     * dropdown
     */
    const shouldDisableServiceCenter = () => {
        if ((editPickup.dealerId.length === 0)) {
            return true;
        }
        else {
            return !(editPickup.id === 0 && userServiceCenter.name === "ALL");
        }                  
    };

    /**
     * Check if we've tried to save already, if so
     * trigger the validation check so the user gets
     * up to date feedback
     */
    const handleBlur = () => {
        if (saveAttempts > 0) {
            checkValidity();
        }
    };

    /**
     * Cast the current Driver as the appropriate type
     * and run the model validator against it
     * @param d The DriverRead object to check
     */
    const checkValidity = () => {

        const tmpDriver = !editPickup.id 
            ? Mapper.deserialize(PickupCreate, editPickup)
            : Mapper.deserialize(PickupUpdate, editPickup);

        // Because our read and edit VMs have different property names
        // we have to explicitly set this property
        tmpDriver.pickupTimeLocal  = editPickup.pickupTime ? editPickup.pickupTime.local : undefined;
        
        const msgs = ModelValidator.validate(tmpDriver);
        setErrors(msgs);
        setSaveAttempts(saveAttempts + 1);

        return AppUtils.IsEmpty(msgs) ? true : false;
    };

    /**
     * Save the changes to the truck before closing
     */
    const onSave = async () => {

        const valid = checkValidity();
        if (valid) {
            const result = await service.savePickup(editPickup);
            if (result.status === StatusCode.Success) {
                if (editPickup.id) {

                    const updatedPickup = {
                        ...editPickup,
                        rowVersion: result.data
                    } as PickupRead;

                    setEditPickup(undefined as unknown as PickupRead);
                    props.onSuccess(updatedPickup);
                }
                else {
                    setEditPickup(undefined as unknown as PickupRead);
                    props.onSuccess(result.data);
                }                
            }
        }

    };

    /**
     * Handler for changing the cutoff date.
     * @param newDate The new cutoff date as chosen 
     */
    const onCutoffDateChange = (newDate: Date) => {
        const tmp = {...editPickup};

        if (!tmp.pickupTime) {
            tmp.pickupTime = new LocalTimeRead();
        }
         
        tmp.pickupTime!.local = newDate;
        setEditPickup(tmp);
    };

    /**
     * Handler for changing a dealer.  Clears out any selected orders
     * and zeroes out the total.
     * @param e The selected autocomplete object.
     */
    const onDealerChange = async (e: any) => {

        if (!e) {
            return;
        }

        const tmp           = {...editPickup};
        tmp.dealerId        = e.id ? e.id.toUpperCase() : '';
        tmp.amount          = 0.00;

        // Fetch that dealer so we can get their service 
        // center to use
        try {
            const dealer = await dealerSvc.getDealer(e.id);            
            if (dealer) {

                // If you aren't all, the pickup gets created in your scope
                // service center always.
                if (userServiceCenter.name !== "ALL") {
                    tmp.serviceCenterId = userServiceCenter.name;
                }
                else {
                    tmp.serviceCenterId = dealer.data.serviceCenterId;
                }
                
                setEditPickup(tmp);                
                await getDealerOrders(e.id, tmp.serviceCenterId);
            }
        }
        catch (err) { 
            console.error(err);
        }        
    };

    /**
     * Cleanup some our state and close the panel
     */
    const onClose = () => {
        setEditPickup(undefined as unknown as PickupRead);
        setErrors({});
        setSaveAttempts(0);
        props.onClose();
    };

    /**
     * Callback from the order selection control
     * @param orders The full list of selected orders
     */
    const onMoveDone = (orders: OrderSummary[]) => {
        
        if (orders) {
            const tmpPickup = {...editPickup} as PickupRead;
            tmpPickup.orders = orders;
            setEditPickup(tmpPickup);
        }
    };
 
    return  <Drawer 
                anchor="right" 
                onClose={ onClose }
                open={ props.visible }>
                    <div className="stop-action-drawer">
                    {
                        editPickup
                            ? <React.Fragment>
                                <FlyoutTitle 
                                    onMobileClose={ onClose }
                                    title={ editPickup.id > 0 ? "Set Basic Information" : "Create Pickup"} />

                                <div className="row">
                                    <div className="col-md-6">
                                        <Forms.DealerInput
                                            id="creditDealer"
                                            label="Dealer"
                                            disable={ editPickup.id > 0 }
                                            dispatch={ dispatch }
                                            onChange={ onDealerChange }
                                            error={ errors.dealerId }
                                            value={ editPickup.dealerId } />
                                    </div>
                                    <div className="col-md-6">
                                        <Forms.SelectInput
                                            id="serviceCenter"
                                            label="Service Center"
                                            onChange={ onServiceCenterChange }
                                            onBlur={ handleBlur }
                                            options={ serviceCenters }
                                            error={ errors.serviceCenterId }
                                            value={ editPickup.serviceCenterId }
                                            disable={ shouldDisableServiceCenter() }/>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Forms.DateTimeInput
                                            id="pickupTime"
                                            format={ GlobalConstants.ABBR_LONG_DATE }
                                            value={ editPickup.pickupTime ? editPickup.pickupTime.local : undefined }
                                            showTimezone={ true }
                                            timezone={ getCorrectPickupTimeZone() }
                                            error={ errors.pickupTimeLocal }
                                            label="Pickup Date"
                                            disable={ false }
                                            onBlur={ handleBlur }
                                            onChange={ onCutoffDateChange }
                                        />
                                    </div>
                                </div>

                                {
                                    editPickup.dealerId.length > 0
                                        ? <FlyoutOrderControl
                                            isFetching={ isFetching }
                                            availableOrders={ availableOrders }
                                            selectedOrders={ selectedOrders }
                                            onMoveDone={ onMoveDone }
                                            entityName="Pickup"
                                        />
                                        : null
                                }

                                <FlyoutSaveCancel
                                    onSave={ onSave }
                                    onCancel={ onClose }
                                />

                            </React.Fragment>
                            : null
                    }
                </div>
            </Drawer>;
};
